import { useEffect, useState } from "react";
import { ShieldAlert, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

/**
 * BLOCK 94 Part C ,  subscriber-only MFA enrollment nudge.
 * Visibility is decided entirely server-side (GET /api/mfa/nudge): the banner
 * only shows for non-admin, non-MFA accounts, is capped at N shows, and the
 * dismissal persists on the user (POST /api/mfa/nudge/dismiss) so it never
 * reappears once dismissed ,  even after re-login on another device.
 */
export default function MfaNudgeBanner() {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api
      .get("/mfa/nudge")
      .then((r) => {
        if (mounted) setShow(!!r.data?.should_show);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const dismiss = async () => {
    setShow(false);
    try {
      await api.post("/mfa/nudge/dismiss");
    } catch {
      /* non-fatal ,  server state already best-effort */
    }
  };

  if (!show) return null;

  return (
    <div
      data-testid="mfa-nudge-banner"
      className="mb-6 flex items-start gap-3 rounded-sm border border-amber-500/30 bg-amber-500/[0.07] px-4 py-3"
    >
      <ShieldAlert className="h-5 w-5 shrink-0 text-amber-400 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-amber-100 font-medium">
          Secure your account with two-factor authentication
        </div>
        <p className="text-xs text-amber-200/70 mt-1">
          Add an authenticator app so a stolen password alone can&apos;t get into
          your Acquigine account. Takes about a minute.
        </p>
        <div className="mt-2.5 flex items-center gap-3">
          <button
            onClick={() => navigate("/app/settings")}
            data-testid="mfa-nudge-enable-btn"
            className="text-xs font-semibold uppercase tracking-widest text-amber-300 hover:text-amber-200 transition-colors"
          >
            Enable MFA →
          </button>
          <button
            onClick={dismiss}
            data-testid="mfa-nudge-later-btn"
            className="text-xs text-amber-200/50 hover:text-amber-200/80 transition-colors"
          >
            Don&apos;t show again
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        data-testid="mfa-nudge-dismiss-btn"
        className="shrink-0 p-1 rounded-sm text-amber-300/60 hover:text-amber-200 hover:bg-amber-500/10 transition-colors"
        title="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
